<template>
  <q-dialog v-model="dialogVisible" persistent @hide="onHide">
    <q-card style="min-width: 400px; max-width: 90vw">
      <q-card-section>
        <div class="text-h6">Select Waveform to Verify</div>
        <div class="text-body2 q-mt-sm">
          Click a waveform to select it, then press OK.
        </div>
      </q-card-section>
      <q-card-section>
        <div v-if="loading" class="row justify-center q-pa-lg">
          <q-spinner-dots color="primary" size="40px" />
        </div>
        <div v-else class="row q-gutter-md justify-center waveform-preview-row">
          <div
            v-for="wf in previews"
            :key="wf.passageWaveformId"
            class="waveform-preview-card"
            :class="{
              'waveform-preview-selected': selectedId === wf.passageWaveformId,
            }"
            @click="selectedId = wf.passageWaveformId"
          >
            <div class="text-caption text-center text-bold q-mb-xs">
              {{ getLabel(wf.passageWaveformType) }}
            </div>
            <canvas
              :ref="'wfCanvas_' + wf.passageWaveformId"
              width="160"
              height="90"
              class="waveform-preview-canvas"
            />
          </div>
        </div>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn flat label="Cancel" class="btn-brand" @click="onCancel" />
        <q-btn
          flat
          label="OK"
          class="btn-brand"
          color="primary"
          :disable="selectedId === null"
          @click="onConfirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DeviceService from '@/services/DeviceService';
import {
  WaveformTypeLabel,
  IWaveformApiResponse,
  IDecodedWaveform,
} from '@/models/PassageWaveform';
import { decodeWaveforms } from '@/utils/WaveformUtils';
import { sortWaveformsForVerify } from '@/utils/PassageVerifyRefresh';

export default defineComponent({
  name: 'WaveformVerifyDialog',
  emits: ['confirm', 'cancel'],
  data() {
    return {
      dialogVisible: false,
      loading: false,
      previews: [] as IDecodedWaveform[],
      selectedId: null as number | null,
      // EN-1510: true once OK or Cancel has emitted for this open.
      resolved: false,
    };
  },
  methods: {
    /**
     * Open the dialog for a passage with multiple waveforms.
     * Fetches waveform data from the API and renders preview canvases.
     */
    open(uid: number, firstWaveformId: number) {
      this.selectedId = null;
      this.previews = [];
      this.resolved = false;
      this.loading = true;
      this.dialogVisible = true;

      DeviceService.getWaveformData(uid, firstWaveformId)
        .then((response) => {
          if (!response.data) {
            this.loading = false;
            return;
          }
          const apiData: IWaveformApiResponse = response.data;
          // EN-1510: fixed ELF, MAG, GEO order; the API order varies.
          this.previews = sortWaveformsForVerify(
            decodeWaveforms(apiData.passageWaveforms)
          );
          this.loading = false;
          this.$nextTick(() => {
            this.renderAllCanvases();
          });
        })
        .catch((error) => {
          console.error('Failed to fetch waveform previews:', error);
          this.loading = false;
        });
    },
    onConfirm() {
      if (this.selectedId !== null) {
        this.resolved = true;
        this.$emit('confirm', this.selectedId);
      }
      this.dialogVisible = false;
    },
    onCancel() {
      this.resolved = true;
      this.$emit('cancel');
      this.dialogVisible = false;
    },
    onHide() {
      // EN-1510: hidden any other way (the dialog is persistent, so not by
      // Esc or the backdrop): still end the pending verify, once.
      if (!this.resolved) {
        this.resolved = true;
        this.$emit('cancel');
      }
      this.previews = [];
      this.selectedId = null;
    },
    getLabel(type: string): string {
      return WaveformTypeLabel[type] || type;
    },
    renderAllCanvases() {
      for (const wf of this.previews) {
        const refKey = 'wfCanvas_' + wf.passageWaveformId;
        const refs = this.$refs[refKey] as HTMLCanvasElement[] | undefined;
        const canvas = refs?.[0];
        if (canvas && wf.data?.length) {
          this.renderCanvas(canvas, wf.data);
        }
      }
    },
    renderCanvas(canvas: HTMLCanvasElement, samples: number[]) {
      const ctx = canvas.getContext('2d');
      if (!ctx || samples.length === 0) return;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, w, h);

      let min = samples[0];
      let max = samples[0];
      for (let i = 1; i < samples.length; i++) {
        if (samples[i] < min) min = samples[i];
        if (samples[i] > max) max = samples[i];
      }
      const range = max - min || 1;
      const padding = 4;
      const plotH = h - padding * 2;
      const plotW = w - padding * 2;

      const step = Math.max(1, Math.floor(samples.length / plotW));

      ctx.beginPath();
      ctx.strokeStyle = '#1976d2';
      ctx.lineWidth = 1;
      let first = true;
      for (let i = 0; i < samples.length; i += step) {
        const x = padding + (i / (samples.length - 1)) * plotW;
        const y = padding + plotH - ((samples[i] - min) / range) * plotH;
        if (first) {
          ctx.moveTo(x, y);
          first = false;
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    },
  },
});
</script>

<style scoped>
.waveform-preview-row {
  flex-wrap: nowrap;
  overflow-x: auto;
}
.waveform-preview-card {
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 6px;
  padding: 8px;
  transition: border-color 0.15s, box-shadow 0.15s;
  background: #fafafa;
  min-width: 176px;
}
.waveform-preview-card:hover {
  border-color: #90caf9;
}
.waveform-preview-selected {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.25);
  background: #e3f2fd;
}
.waveform-preview-canvas {
  display: block;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}
</style>
